import { ChevronRightIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { differenceInCalendarMonths, parse } from "date-fns";
import { useState } from "react";

import { useAuth } from "~/contexts/AuthContext";
import { FoldSection } from "~/modules/dashboard/components/FoldSection.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { getMonthNameFromNumber } from "~/utils/dateAndTimeUtils.ts";

const VISIBLE_ROW_COUNT = 3;
const OLD_MONTH_AGE = 3;
const SECOND_MONTH_AGE = 2;

function getTodoText(age: number) {
	if (age >= OLD_MONTH_AGE) return `Seit ${age} Monaten offen – bitte schließen`;
	return age === SECOND_MONTH_AGE ? "Monatsabschluss noch offen" : "Monatsabschluss offen";
}

/**
 * The full close of an earlier month is enforced only on the screen: the backend refuses no tracking
 * because an earlier month is open. So the text gets stricter the older the open month is.
 * The dashboard shows this section instead of `OpenEarlierMonthsReminder`, so one month never shows two
 * warnings on one page. The text addresses the user, so the section shows on the own dashboard only.
 * The section shows only when a month is open: a to-do is a call to act, and an empty list asks for nothing.
 * So it has no loader and no empty message, and it does not render while the read loads.
 */
export function TodoSection() {
	const user = useDashboardUser();
	const { user: authUser } = useAuth();
	const { goToMonth, setView, scrollToCalendar } = useDashboardCalendar();
	const [isExpanded, setIsExpanded] = useState(false);
	const isOwnDashboard = !!user && authUser?.id === user.id;
	const { data } = useQuery({ ...monthlyClosingQueries.openMonths(user?.id ?? ""), enabled: isOwnDashboard });

	const today = new Date();
	// The read gives the newest month first. The oldest month is the most urgent, so it goes first.
	const openMonths = [...(data?.months ?? [])].reverse().map((key) => {
		const date = parse(key, "yyyy-MM", today);
		return { key, date, age: differenceInCalendarMonths(today, date) };
	});

	if (!isOwnDashboard || openMonths.length === 0) return null;

	const isCollapsed = openMonths.length > VISIBLE_ROW_COUNT && !isExpanded;
	const rows = isCollapsed ? openMonths.slice(0, VISIBLE_ROW_COUNT) : openMonths;
	const hiddenCount = openMonths.length - VISIBLE_ROW_COUNT;

	return (
		<div className="lg:mt-8">
			<FoldSection title="To-dos"
						 storageKey="dashboard-todos-open"
						 count={openMonths.length}>
				<ul className="divide-y divide-amber-200 overflow-hidden rounded-lg border border-amber-200 bg-amber-50/60">
					{rows.map((month) => {
						const isOld = month.age >= OLD_MONTH_AGE;
						return (
							<li key={month.key}>
								<button type="button"
										className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-amber-100/60"
										onClick={() => {
											goToMonth(month.key);
											setView("month");
											// On a phone the calendar is far below this section.
											scrollToCalendar();
										}}>
									<LockClosedIcon className={clsx("size-4 shrink-0", isOld ? "text-red-600" : "text-amber-600")} />
									<span className="flex min-w-0 flex-1 flex-col text-sm sm:flex-row sm:gap-1">
										<span className="font-semibold text-gray-900">
											{getMonthNameFromNumber(month.date.getMonth())} {month.date.getFullYear()}
										</span>
										<span className={clsx(isOld ? "font-medium text-red-600" : "text-gray-700")}>
											<span className="hidden sm:inline">· </span>{getTodoText(month.age)}
										</span>
									</span>
									<span className="flex shrink-0 items-center text-xs font-medium text-primary-400">
										Zum Monat <ChevronRightIcon className="size-4" />
									</span>
								</button>
							</li>
						);
					})}
					{isCollapsed && (
						<li>
							<button type="button"
									className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-amber-100/60"
									onClick={() => setIsExpanded(true)}>
								{hiddenCount} weitere offene {hiddenCount === 1 ? "Monat" : "Monate"}
							</button>
						</li>
					)}
				</ul>
			</FoldSection>
		</div>
	);
}
